import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import toast from "react-hot-toast";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  useCreateAboutMutation,
  useGetAboutByIdQuery,
  useUpdateAboutMutation,
} from "../../../features/api/aboutApiSlice";
import LoadingSpinner from "../../loader/Loader";

import { useRecaptcha, executeRecaptcha } from "../../../hooks/useRecaptcha";

const SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

const AdminAddEditAbout = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [editingMode, setEditingMode] = useState(false);

  useRecaptcha(SITE_KEY, [`/admin/about/editabout/${id}`]); // ✅ only show badge on /login

  const [createAbout, { isLoading }] = useCreateAboutMutation();
  const [updateAbout, { isLoading: isLoadingUpdating }] =
    useUpdateAboutMutation();

  const { data: about } = useGetAboutByIdQuery(id, {
    skip: !id, // Skip query if no id is provided
  });

  useEffect(() => {
    if (id) {
      setEditingMode(true);
    }
  }, [id]);

  // Handle form submission
  const handleSubmit = async (values) => {
    try {
      const token = await executeRecaptcha(
        SITE_KEY,
        `/admin/about/editabout/${id}`
      );

      if (editingMode) {
        const response = await updateAbout({
          id,
          ...values,
          recaptchaToken: token,
        }).unwrap();

        toast.success(response?.message || "About updated successfully!");
        navigate("/admin/about");
      } else {
        const response = await createAbout({
          ...values,
          recaptchaToken: token,
        }).unwrap();

        toast.success(response?.message || "About added successfully!");
        navigate("/admin/about");
      }
    } catch (error) {
      toast.error(
        error?.data?.message || "An error occurred, please try again."
      );
    }
  };

  if (isLoading || isLoadingUpdating) return <LoadingSpinner />;

  // add schema validation
  const validateAbout = Yup.object().shape({
    description: Yup.string().required("Description is required"),
  });

  return (
    <div className="dashboard-wrapper">
      <div className="dashboard-top-section">
        <div className="dash-top-flex">
          <h1 className="dashboard-title">
            {editingMode ? "Edit About" : "Add About"}
          </h1>
          <button onClick={() => navigate("/admin/about")}>About</button>
        </div>
      </div>
      <section className="dashboard-overview">
        <Formik
          initialValues={{
            id,
            description: about?.description || "",
          }}
          validationSchema={validateAbout}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ isSubmitting, values, setFieldValue }) => (
            <Form>
              <div>
                <label htmlFor="description">Description:</label>
                <ReactQuill
                  className="custom-quill-editor"
                  value={values.description || ""}
                  onChange={(value) => setFieldValue("description", value)}
                  name="description"
                />
                <ErrorMessage
                  name="description"
                  component="div"
                  className="error"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="form-button"
              >
                {editingMode ? "Update About" : "Add About"}
              </button>
            </Form>
          )}
        </Formik>
      </section>
    </div>
  );
};

export default AdminAddEditAbout;
